import React, { useState, useCallback } from 'react';
import { Bell, Download } from 'lucide-react';
import { Button } from 'components/common/Button';
import { StoreDataService } from 'services/data/store-data.service';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { AlertService } from 'services/alerts/alert.service';
import { AlertCard } from 'components/alerts/AlertCard';
import { LoadingScreen } from 'components/common/Spinner';
import { Alert, AlertStatus } from 'dtos/alerts/alert.dto';
import { PaginatedResponse } from 'dtos/common/api.dto';
import { Pagination } from 'components/common/Pagination';

const PAGE_SIZE = 8;

const STATUS_TABS: { key: AlertStatus | 'ALL'; label: string }[] = [
  { key: 'NEW', label: 'New' },
  { key: 'ACKNOWLEDGED', label: 'Seen' },
  { key: 'RESOLVED', label: 'Fixed' },
  { key: 'DISMISSED', label: 'Not relevant' },
  { key: 'ALL', label: 'All' },
];

export default function AlertsPage() {
  const { org, currentStore } = useStore();
  const [activeTab, setActiveTab] = useState<AlertStatus | 'ALL'>('NEW');
  const [alerts, setAlerts] = useState<PaginatedResponse<Alert> | null>(null);
  const [page, setPage] = useState(1);

  const { loading } = useApi(
    () => AlertService.list(org?.id ?? '', currentStore?.id ?? '', activeTab === 'ALL' ? undefined : activeTab, page, PAGE_SIZE).then(data => { setAlerts(data); return data; }),
    [currentStore?.id, activeTab, page]
  );

  const [actionError, setActionError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const downloadPriceChanges = async () => {
    setDownloading(true); setActionError('');
    try { await StoreDataService.downloadPriceChanges(org?.id ?? '', currentStore?.id ?? ''); }
    catch (err: any) { setActionError(err?.message || 'The price-change sheet could not be downloaded.'); }
    finally { setDownloading(false); }
  };

  // An alert that changes status leaves the filtered tab.
  const updateAlert = useCallback((updated: Alert) => {
    setAlerts(prev => prev ? {
      ...prev,
      data: activeTab === 'ALL' || updated.status === activeTab ? prev.data.map(a => a.id === updated.id ? updated : a) : prev.data.filter(a => a.id !== updated.id),
      meta: activeTab === 'ALL' || updated.status === activeTab ? prev.meta : { ...prev.meta, total: Math.max(prev.meta.total - 1, 0) },
    } : null);
  }, [activeTab]);

  const act = (action: (orgId: string, id: string) => Promise<Alert>) => async (id: string) => {
    setActionError('');
    try { updateAlert(await action(org?.id ?? '', id)); }
    catch (err: any) { setActionError(err?.message || 'That change could not be saved. Please try again.'); }
  };
  const handleAcknowledge = act(AlertService.acknowledge);
  const handleResolve = act(AlertService.resolve);
  const handleDismiss = act(AlertService.dismiss);

  if (loading && !alerts) return <LoadingScreen />;

  const filtered = alerts?.data ?? [];
  const counts = STATUS_TABS.reduce((acc, t) => {
    acc[t.key] = t.key === activeTab ? (alerts?.meta.total ?? 0) : 0;
    return acc;
  }, {} as Record<string, number>);

  const totalImpact = filtered.filter(a => a.status !== 'RESOLVED' && a.status !== 'DISMISSED').reduce((s, a) => s + (a.estimated_monthly_impact ?? 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell size={20} className="text-gray-600" />
          <h1 className="text-xl font-bold text-gray-900">Profit Leaks</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {totalImpact > 0 && (
            <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
              On this page: <strong>~${Math.round(totalImpact).toLocaleString()}/mo</strong> at stake
            </div>
          )}
          <Button size="sm" variant="secondary" onClick={() => void downloadPriceChanges()} loading={downloading}
            title="A spreadsheet of every suggested new shelf price (barcode, item, current and new price) to key into your POS">
            <Download size={14} /> Price-change sheet
          </Button>
        </div>
      </div>

      {/* Tabs */}
      {actionError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setPage(1); }}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${activeTab === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Alert list */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            {activeTab === 'NEW' ? 'No new profit leaks. Pellvr re-checks your costs and prices every night.' : 'Nothing here yet.'}
          </div>
        ) : (
          filtered.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onDismiss={handleDismiss}
              />
            ))
        )}
      </div>
      {alerts && <Pagination page={page} pageSize={PAGE_SIZE} total={alerts.meta.total} onPageChange={setPage} />}
    </div>
  );
}

